import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ChevronDown, X } from "lucide-react";

export default function DetailsPopover({
  children,
  label,
}: {
  children: ReactNode;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const outside = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  return (
    <div
      className="et-details"
      ref={ref}
      onBlur={(e) => {
        if (
          e.relatedTarget &&
          !e.currentTarget.contains(e.relatedTarget as Node)
        )
          setOpen(false);
      }}
    >
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls={id}
        aria-label={`Details for ${label}`}
        onClick={() => setOpen(!open)}
      >
        Details <ChevronDown size={12} />
      </button>
      {open && (
        <div
          id={id}
          className="et-popover"
          role="region"
          aria-label={`Details for ${label}`}
        >
          <div className="flex justify-between items-center mb-3">
            <strong>{label}</strong>
            <button
              type="button"
              className="et-icon-button"
              aria-label="Close details"
              onClick={() => {
                setOpen(false);
                button.current?.focus();
              }}
            >
              <X size={15} />
            </button>
          </div>
          {children}
        </div>
      )}
    </div>
  );
}
